import * as React from 'react';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {  Button, LinearProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AlumnoType } from '../../types/types';
import hash from 'object-hash';
import { ReporteDeudaType, ReportePagosType, useGetReporteDeuda } from '../../hooks/getDeuda';
import { useContext, useEffect, useState } from 'react';
import { AddPagosPayloadType, UseAddPAgo } from '../../hooks/addPAgo';
import { UserContext } from '../../contexts/userContext';
import { PagoDialog } from './DialogPago';
import dayjs from 'dayjs';
import { monthToString } from '../../utils/utils';
import { TablaPagos } from './tablaPAgos';
import FullScreenDialog from '../FullScreenDialog';
import { Recibo } from './Recibo';
import { PostMesInactivoParams, usePostMesInactivoAlumno } from '../../hooks/usePostMesInactivo';
import { useQueryClient } from 'react-query';
import { alumnoApiCall } from '../../hooks/getAlumnos';


export interface DeudaReportProps {
  alumno: AlumnoType | null;
  refetchLista: () => void;
  verHistorico: boolean;
}

export const emptyPago = {
  open: false,
  anio: 0,
  mes: 0,
  user: '',
  monto: 0,
  montoEfectivo: 0,
  montoTransferencia: 0,
  obs: '',
  alumnoId: '',
  fecha: dayjs().format('YYYY-MM-DD'),
  transferencia: false,
  saldado: false,
};

export default function DeudaReport(props: DeudaReportProps) {
  const { alumno, refetchLista, verHistorico } = props;
  const { user } = useContext(UserContext);
  const [pago, setPago] = useState<AddPagosPayloadType[] | null>(null);
  const [nuevoPago, setNuevoPago] = useState(emptyPago);
  const [dialogPagos, setDialogPagos] = useState<ReportePagosType[] | null>(null);
  const addPago = UseAddPAgo(pago, user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const queryClient = useQueryClient();

  const deudas = useGetReporteDeuda(alumno?._id ? [alumno?._id] : null, user, verHistorico);

  const [ mutableActivo, setMutableActivo ] = useState<PostMesInactivoParams | null>();
  const activoApiCall = usePostMesInactivoAlumno(mutableActivo, user);

  const [recibo, setRecibo] = useState<ReporteDeudaType | null>(null);

  const submitPago = () => {
    if (alumno?.dni && nuevoPago.anio > 0) {
      setPago([{
        ...nuevoPago,
        user: user?._id ?? '',
        monto: nuevoPago.monto,
        alumnoId: alumno._id,
      }]);
    }
  };

  useEffect(() => {
    if (addPago.data && !addPago.isLoading) {
      deudas.refetch();
      refetchLista();
      setNuevoPago(emptyPago);
      if (dialogPagos) setDialogPagos(null);
    }
  }, [addPago.data]);

  useEffect(() => {
    if (!activoApiCall.isLoading) {
      console.log('refresh');
      deudas.refetch();
    }
  }, [activoApiCall.isLoading]);

  useEffect(() => {
    if ((addPago.data && !addPago.isFetching) || (activoApiCall.data && !activoApiCall.isFetching)) {
      if (alumno) {
        alumnoApiCall(user, alumno?._id).then(updatedAlumno => {
          if (!updatedAlumno) return;
          queryClient.setQueryData(['getAlumno', hash(user ?? ''), alumno?._id], updatedAlumno);

          queryClient.setQueryData(['getAlumnos', hash(user ?? ''), false, verHistorico], (oldList:AlumnoType[] = []) => {
            return oldList.map(a => a._id === alumno?._id ? updatedAlumno : a);
          });
        });
      }
    }
  }, [addPago.data, activoApiCall.data, alumno, user, queryClient, verHistorico]);


  if (!alumno) return null;
  if (!user?._id) return null;

  const columns: GridColDef[] = [
    {
      field: 'periodo',
      headerName: 'Periodo',
      valueGetter: (params) => dayjs(`${params.row.year}-${params.row.month}-01`).valueOf(),
      headerClassName: 'table-headers',
      width: 180,
      renderHeader: isMobile ? undefined : () => (<strong>Periodo</strong>),
      renderCell: (params: { row: ReporteDeudaType }) => {
        const monto = params.row.monto;
        const saldado = params.row.saldado;
        const pagos:ReportePagosType[] = params.row.pagos;
        const totalPagado = pagos ? pagos.reduce((ac, p) => Number(ac) + Number(p.importe), 0) : 0;
        const deuda = monto - totalPagado;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div style={{ fontWeight: 'bold', marginTop: 16, marginBottom: 16 }}>
              {params.row.esMatricula &&
              <>
                {'MATRICULA'}
                <br/>
                {<span style={{ fontSize: '11px' }}>{`(Al valor de ${monthToString(params.row.month)} de ${params.row.year})`}</span>}
              </>
              }
              {params.row.esSeguro &&
              <>
                {'SEGURO'}
                <br/>
                {<span style={{ fontSize: '11px' }}>{`(Al valor de ${monthToString(params.row.month)} de ${params.row.year})`}</span>}
              </>
              }
              {!params.row.esMatricula && !params.row.esSeguro && `${monthToString(params.row.month)} de ${params.row.year}`}
            </div>
            {!params.row.esMatricula && params.row.inactivo && <div style={{ width: '100%' }}>
              <Button
                fullWidth
                variant='contained'
                size='small'
                color={'success'}
                onClick={() => {
                  setMutableActivo({
                    id: params.row.alumnoId,
                    mes: params.row.month,
                    anio: params.row.year,
                    inactivo: false,
                  });
                }}
                style={{ fontSize: 8, padding: 2, marginTop: 0, marginBottom: 8 }}
              >
                {<>Marcar como activo este mes</>}
              </Button>
            </div>
            }
            {!params.row.error && !params.row.inactivo && <div>
              <Button
                fullWidth
                variant='contained'
                size='small'
                color={deuda > 0 && !saldado ? 'error' : 'success'}
                onClick={deuda <= 0 && !saldado ? ()=>{}
                  : () => {
                    setNuevoPago((prev) => ({
                      ...prev,
                      open: true,
                      monto,
                      montoEfectivo: params.row.monto,
                      montoTransferencia: params.row.montoTransferencia,
                      mes: params.row.month,
                      anio: params.row.year,
                      esMatricula: params.row.esMatricula,
                      esSeguro: params.row.esSeguro,
                    }));
                  }}
                style={{ fontSize: 'x-small', marginTop: 0, marginBottom: 8 }}
              >
                {deuda > 0 && !saldado ? <>Registrar pago</> : <>Saldado</>}
              </Button>
              {deuda > 0 && !saldado && <Button
                fullWidth
                variant='contained'
                size='small'
                onClick={() => {
                  setPago([{
                    ...nuevoPago,
                    user: user?._id ?? '',
                    alumnoId: alumno._id,
                    monto: params.row.monto,
                    montoEfectivo: params.row.monto,
                    montoTransferencia: params.row.montoTransferencia ?? params.row.monto,
                    mes: params.row.month,
                    anio: params.row.year,
                    obs: 'Marcado como saldado',
                    saldado: true,
                    esMatricula: params.row.esMatricula,
                    esSeguro: params.row.esSeguro,
                  }]);
                }}
                style={{ fontSize: 'x-small', marginTop: 0, marginBottom: 8 }}
              >
              Marcar como saldado
              </Button>}
              {params.row.pagos.length > 0 &&
                <Button
                  fullWidth
                  variant='contained'
                  size='small'
                  onClick={() => setRecibo(params.row)}
                  style={{ fontSize: 'x-small', marginBottom: 8 }}
                >
                  Generar Recibo
                </Button>
              }
              {!params.row.error && isMobile && (
                <>
                  <Button
                    fullWidth
                    variant='contained'
                    size='small'
                    color='success'
                    onClick={() => setDialogPagos(params.row.pagos)}
                    style={{ fontSize: 'x-small', marginBottom: 8 }}
                  >
                    Ver pagos
                  </Button>
                  {params.row.pagos.length > 0 &&
                <Button
                  fullWidth
                  variant='contained'
                  size='small'
                  onClick={() => setRecibo(params.row)}
                  style={{ fontSize: 'x-small', marginBottom: 8 }}
                >
              Generar Recibo
                </Button>
                  }
                </>
              )}
            </div>}
            {!params.row.esMatricula && !params.row.inactivo && !params.row.pagos?.length && <div>
              <Button
                fullWidth
                variant='contained'
                size='small'
                onClick={() => {
                  setMutableActivo({
                    id: params.row.alumnoId,
                    mes: params.row.month,
                    anio: params.row.year,
                    inactivo: true,
                  });
                }}
                style={{ fontSize: 8, padding: 2, marginTop: 0, marginBottom: 8, backgroundColor: theme.palette.grey[700] }}
              >
                {<>Marcar como inactivo este mes</>}
              </Button>
            </div>
            }
          </div>);
      },
    },
    {
      field: 'monto',
      headerName: 'Monto a pagar',
      headerClassName: 'table-headers',
      width: 180,
      sortable: false,
      renderHeader: isMobile ? undefined : () => (<strong>Monto a pagar</strong>),
      cellClassName: 'mobileCell',
      renderCell: (params) =>{
        if (params.row.inactivo) return '';
        if (params.row.error) return (
          <div style={{ width: isMobile ? '100%' : '300px' }}>
            <Typography color='error'>{params.row.error}</Typography>
          </div>
        );
        return ( params.row.error ? '' :
          <div>
            <span style={{ whiteSpace: 'nowrap', fontSize: 17 }}>
              {`Monto $${params.row.montoOriginal.toFixed(2)}`}
            </span>
            {params.row.descuento > 0 &&
          <>
            <br />
            <span style={{ whiteSpace: 'nowrap', fontSize: 14 }}>
              {`${params.row.descuento.toFixed(2)}% descuento`}
            </span>
            <br />
            <span style={{ whiteSpace: 'nowrap', fontSize: 18 }}>
              {`Final: $${params.row.monto.toFixed(2)}`}
            </span>
          </>
            }
          </div>);
      },
    },
    {
      field: 'pagos',
      headerName: 'Pagos',
      headerClassName: 'table-headers',
      flex: 1,
      sortable: false,
      renderHeader: isMobile ? undefined : () => (<strong>Pagos</strong>),
      minWidth: isMobile ? undefined : 550,
      hide: isMobile,
      renderCell: (params:{ row: ReporteDeudaType }) => {
        if (params.row?.error) return '';
        if (params.row.inactivo) return <Typography>Durante este mes el alumno estuvo INACTIVO</Typography>;
        if (!params.row.pagos?.length) return (
          <div style={{ width: isMobile ? '100%' : '300px'  }}>
            <Typography color='error'>No hay pagos registrados para este periodo</Typography>
          </div>
        );
        return (<TablaPagos pagos={params.row.pagos} id={params.row._id} refresh={() => {deudas.refetch(); refetchLista();}} />);
      },
    },
    {
      field: 'adeudado',
      headerName: 'Adeudado',
      headerClassName: 'table-headers',
      width: 120,
      sortable: false,
      renderHeader: isMobile ? undefined : () => (<strong>Adeudado</strong>),
      renderCell: (params) => {
        if (params.row.error || params.row.inactivo) return '';
        const monto = params.row.monto;
        const pagos:ReportePagosType[] = params.row.pagos;
        const totalPagado = pagos.reduce((ac, p) => Number(ac) + Number(p.importe), 0);
        const deuda = monto - totalPagado;
        if (params.row.saldado || deuda <= 0) return <Typography fontSize={20} color={theme.palette.success.main}>saldado</Typography>;
        return <Typography fontSize={20} color={deuda > 0 ? 'error' : 'success'}>{`$${deuda.toFixed(2)}`}</Typography>;
      },
    },
  ];

  if (!deudas.data || deudas.isLoading) return <LinearProgress />;

  const deudasOrdenadas = [...deudas.data].sort((a, b) => {
    const fechaA = dayjs(`${a.year}-${(a.month ?? 1).toString().padStart(2, '0')}`);
    const fechaB = dayjs(`${b.year}-${(b.month ?? 1).toString().padStart(2, '0')}`);

    if (fechaA.isAfter(fechaB)) return -1;
    if (fechaA.isBefore(fechaB)) return 1;

    // Si las fechas son iguales, compara por tipo de pago (cuota antes de matricula/seguro)
    const aEspecial = a.esMatricula || a.esSeguro ? 1 : 0;
    const bEspecial = b.esMatricula || b.esSeguro ? 1 : 0;
    return aEspecial === bEspecial ? 0 : aEspecial > bEspecial ? 1 : -1;
  });

  return (
    <>
      <Recibo onClose={() => {setRecibo(null);}} pagos={recibo} alumno={alumno} />
      <FullScreenDialog titulo="Lista de pagos" open={!!dialogPagos} onClose={() => setDialogPagos(null)}>
        {dialogPagos && <TablaPagos pagos={dialogPagos}/>}
      </FullScreenDialog>
      <PagoDialog
        isLoading={addPago.isLoading}
        refetch={() => submitPago()}
        nuevoPago={nuevoPago}
        setNuevoPago={setNuevoPago}
        montoEfectivo={nuevoPago.montoEfectivo}
        montoTransferencia={nuevoPago.montoTransferencia}
      />
      <DataGrid
        // initialState={{
        //   sorting: {
        //     sortModel: [{ field: 'periodo', sort: 'desc' }],
        //   },
        // }}
        disableSelectionOnClick
        autoHeight
        getRowId={(row) =>  hash(row)}
        getRowHeight={() => 'auto'}
        rows={deudasOrdenadas}
        columns={columns}
        components={{ Toolbar: GridToolbar }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        hideFooter
      />
    </>
  );
}
